'use client';

import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React, { useMemo, useState } from 'react';

export type JobportalCheckboxesFilterOption = {
  label: string;
  value: string;
};

type JobportalCheckboxesFilterProps = {
  title?: string;
  options: JobportalCheckboxesFilterOption[];
  selectedOptions: string[];
  onChange: (selected: string[]) => void;
  search?: boolean;
  searchPlaceholder?: string;
  sortBySelected?: boolean;
  className?: string;
};

export function CheckboxesFilter(props: JobportalCheckboxesFilterProps) {
  const [query, setQuery] = useState('');

  const options = useMemo(() => {
    let arr = props.options;
    if (props.search && query.trim()) {
      const q = query.toLowerCase();
      arr = arr.filter(o => o.label.toLowerCase().includes(q));
    }
    if (props.sortBySelected) {
      const set = new Set(props.selectedOptions);
      arr = [...arr].sort((a, b) => Number(set.has(b.value)) - Number(set.has(a.value)));
    }
    return arr;
  }, [props.options, props.search, props.sortBySelected, props.selectedOptions, query]);

  const toggle = (value: string) => {
    const set = new Set(props.selectedOptions);
    if (set.has(value)) set.delete(value);
    else set.add(value);
    props.onChange(Array.from(set));
  };

  return (
    <div className={cn('flex w-full flex-col gap-3', props.className)}>
      {props.title && (
        <div className="text-start text-lg font-semibold text-[#63626F]">{props.title}</div>
      )}
      {props.search && (
        <Input
          placeholder={props.searchPlaceholder || ''}
          value={query}
          onChange={e => setQuery(e.target.value)}
          className="h-9"
        />
      )}
      <div className="flex max-h-80 flex-col gap-2 overflow-y-auto">
        {options?.map?.(opt => {
          const checked = props.selectedOptions.includes(opt.value);
          return (
            <label key={opt.value} className="flex cursor-pointer items-center gap-3 text-sm">
              <Checkbox checked={checked} onCheckedChange={() => toggle(opt.value)} />
              <span className={cn('truncate', checked && 'font-semibold text-foreground')}>
                {opt.label}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
